import React, { useState, useEffect, useRef, useCallback } from 'react'
import { X } from 'lucide-react'
import { Text, Button, Group } from '@mantine/core'
import { mantineTheme } from '@/config/mantineTheme'
import { format } from 'date-fns'
import CallerInformation, {
  CallerInformationFormValues,
} from './CallerInformation'
import AmbulanceRequestInformation, {
  AmbulanceRequestInformationFormValues,
} from './AmbulanceRequestInformation'
// Form submission is now handled by individual form onSubmit handlers
import GuidedProtocolQuestions from './GuidedProtocolQuestions'
import Notes from './Notes'
import { useCaseCreationStore } from '@/store/useCaseCreationStore'
import { useCreateCase } from '@/hooks/api/useCases'
import {
  combineCaseData,
  transformCallerOnlyData,
} from '@/utils/caseDataTransformation'

interface CaseCreationModalProps {
  isOpen: boolean
  onClose: () => void
}

const CaseCreationModal: React.FC<CaseCreationModalProps> = ({
  isOpen,
  onClose,
}) => {
  // Get store state and actions
  const {
    callerInformation,
    ambulanceRequestInformation,
    notes,
    isSubmitting,
    setCallerInformation,
    setAmbulanceRequestInformation,
    setCurrentSection,
    clearCase,
    hasCallerData,
    setSubmitting,
  } = useCaseCreationStore()

  // API hook for creating cases
  const createCaseMutation = useCreateCase()

  // Local state for section management (synced with store)
  const [section, setSection] = useState<
    'caller-information' | 'ambulance-request-information'
  >('caller-information')
  const [callDuration, setCallDuration] = useState('00:00:00')
  const callTimerRef = useRef<number | null>(null)
  const stopCallDurationTimer = useCallback(() => {
    if (callTimerRef.current) {
      window.clearInterval(callTimerRef.current)
      callTimerRef.current = null
    }
  }, [])

  const startCallDurationTimer = useCallback(() => {
    stopCallDurationTimer()
    const startedAt = Date.now()
    callTimerRef.current = window.setInterval(() => {
      const diff = Date.now() - startedAt
      const hours = Math.floor(diff / 3600000)
      const minutes = Math.floor((diff % 3600000) / 60000)
      const seconds = Math.floor((diff % 60000) / 1000)
      setCallDuration(
        `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
      )
    }, 1000)
  }, [stopCallDurationTimer])

  const handleCallerDataChange = (values: CallerInformationFormValues) => {
    // Store caller data in the store
    setCallerInformation(values)
  }

  const handleAmbulanceDataChange = (
    values: AmbulanceRequestInformationFormValues
  ) => {
    // Store ambulance data in the store
    setAmbulanceRequestInformation(values)
  }

  const handleCallerSubmit = async (values: CallerInformationFormValues) => {
    // Store caller data in the store
    setCallerInformation(values)

    // If ambulance request, switch to ambulance section
    if (values.call_type === 'ambulance_request') {
      setSection('ambulance-request-information')
      setCurrentSection('ambulance-request-information')
    } else {
      // For non-ambulance requests, submit immediately
      try {
        setSubmitting(true)
        const caseData = transformCallerOnlyData(values, notes)

        await createCaseMutation.mutateAsync(caseData)
        stopCallDurationTimer()

        // Success - clear store, reset section state, and close modal
        clearCase()
        setSection('caller-information')
        setCurrentSection('caller-information')
        onClose()
      } catch (error) {
        console.error('Failed to create case:', error)
        // Error handling is done by the mutation hook (toast notification)
      } finally {
        setSubmitting(false)
      }
    }
  }

  const handleAmbulanceSubmit = async (
    values: AmbulanceRequestInformationFormValues
  ) => {
    // console.log('=== AMBULANCE FORM SUBMITTED ===')
    // console.log('Form values:', values)
    // console.log('Dropoff coordinates:', {
    //   lat: values.dropoff_location_latitude,
    //   lng: values.dropoff_location_longitude,
    // })
    // console.log('Pickup coordinates:', {
    //   lat: values.pickup_location_latitude,
    //   lng: values.pickup_location_longitude,
    // })

    // Store ambulance data in the store
    setAmbulanceRequestInformation(values)

    if (!callerInformation) {
      console.error('No caller information available for ambulance request')
      return
    }

    try {
      setSubmitting(true)

      // Combine caller and ambulance data
      const caseData = combineCaseData(
        callerInformation as CallerInformationFormValues,
        values,
        notes
      )

      await createCaseMutation.mutateAsync(caseData)
      stopCallDurationTimer()

      // Success - clear store, reset section state, and close modal
      clearCase()
      setSection('caller-information')
      setCurrentSection('caller-information')
      onClose()
    } catch (error) {
      console.error('Failed to create case:', error)
      // Error handling is done by the mutation hook (toast notification)
    } finally {
      setSubmitting(false)
    }
  }

  const handleCancel = () => {
    // If we're in ambulance section and have caller data, go back to caller section
    // This allows users to edit caller information after they've started ambulance section
    if (section === 'ambulance-request-information' && hasCallerData()) {
      setSection('caller-information')
      setCurrentSection('caller-information')
      return
    }

    // Otherwise, clear all data, reset section state, and close modal
    stopCallDurationTimer()
    clearCase()
    setSection('caller-information') // Reset local section state
    setCurrentSection('caller-information') // Reset store section state
    onClose()
  }

  // Handle the main submit button click - triggers form submission
  const handleMainSubmit = () => {
    // Find the form element and trigger its submit event
    if (section === 'caller-information') {
      const form = document.querySelector(
        '#caller-information-form'
      ) as HTMLFormElement
      if (form) {
        form.requestSubmit()
      }
    } else if (section === 'ambulance-request-information') {
      const form = document.querySelector(
        '#ambulance-request-information-form'
      ) as HTMLFormElement
      if (form) {
        form.requestSubmit()
      }
    }
  }

  // Reset state when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      // Always start from caller-information when modal opens
      setSection('caller-information')
      setCurrentSection('caller-information')
      setCallDuration('00:00:00')
      startCallDurationTimer()
    } else {
      stopCallDurationTimer()
      setCallDuration('00:00:00')
    }
    return () => {
      stopCallDurationTimer()
    }
  }, [isOpen, setCurrentSection, startCallDurationTimer, stopCallDurationTimer])

  if (!isOpen) return null

  return (
    <div
      className="fixed inset-0 flex items-center justify-center  bg-black/35 z-50 p-4"
      // onClick={onClose}
    >
      <div
        className="flex flex-col bg-white rounded-lg shadow-xl min-w-4/5 max-w-11/12 w-full max-h-[90vh] p-6 gap-4 overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        {/* Header – shrink-0 so body gets remaining space */}
        <div className="flex flex-col gap-2 shrink-0">
          <div className="flex items-center justify-between">
            <Text
              size="lg"
              fw={600}
              c={mantineTheme.other?.customColors.offBlack}
            >
              Case Creation
            </Text>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              aria-label="Close modal"
            >
              <X size={20} className="text-gray-500 cursor-pointer" />
            </button>
          </div>

          {/* Sub Header */}
          <div className="flex items-center justify-between">
            <Text size="sm" fw={500} c="grey.11">
              Emergency Medical Services - Guided Dispatch
            </Text>
            <Text size="sm" fw={500} c="grey.11">
              Dispatcher: {format(new Date(), 'M/d/yyyy, h:mm:ss a')}
            </Text>
          </div>
        </div>

        {/* Body – flex-1 min-h-0 so it takes remaining space and scrolls internally */}
        <div className="flex justify-between gap-4 flex-1 min-h-0 overflow-y-auto">
          {/* Caller Information */}
          {section === 'caller-information' && (
            <div className="flex-2 min-w-0 min-h-0">
              <CallerInformation
                mode="standalone"
                onChange={handleCallerDataChange}
                onSubmit={handleCallerSubmit}
                callDuration={callDuration}
                initialValues={{
                  // Use stored data if available, otherwise use defaults
                  call_direction:
                    callerInformation?.call_direction || 'incoming',
                  phone_number:
                    callerInformation?.phone_number || '+23490000000',
                  call_duration: callerInformation?.call_duration || '00:00:00',
                  gender: callerInformation?.gender || 'male',
                  caller_history:
                    callerInformation?.caller_history ||
                    'No previous cases found for this caller',
                  prefix: callerInformation?.prefix || 'mr',
                  first_name: callerInformation?.first_name || '',
                  last_name: callerInformation?.last_name || '',
                  date_of_birth: callerInformation?.date_of_birth || undefined,
                  location: callerInformation?.location || '',
                  call_type: callerInformation?.call_type || undefined,
                  call_category: callerInformation?.call_category || undefined,
                }}
              />
            </div>
          )}

          {/* Ambulance Request Information */}
          {section === 'ambulance-request-information' && (
            <div className="flex-2 min-w-0 min-h-0">
              <AmbulanceRequestInformation
                mode="standalone"
                onChange={handleAmbulanceDataChange}
                onSubmit={handleAmbulanceSubmit}
                callerInformation={callerInformation}
                initialValues={{
                  ...ambulanceRequestInformation,
                  pickup_location:
                    ambulanceRequestInformation?.pickup_location || '',
                  destination: ambulanceRequestInformation?.destination || '',
                  first_name: ambulanceRequestInformation?.first_name || '',
                  last_name: ambulanceRequestInformation?.last_name || '',
                  date_of_birth:
                    ambulanceRequestInformation?.date_of_birth ||
                    new Date('2025-09-19'),
                  age: ambulanceRequestInformation?.age || 0,
                  weight: ambulanceRequestInformation?.weight || 0,
                  gender: ambulanceRequestInformation?.gender || 'male',
                  is_conscious:
                    ambulanceRequestInformation?.is_conscious || 'yes',
                  oxygen_required:
                    ambulanceRequestInformation?.oxygen_required || 'yes',
                }}
              />
            </div>
          )}

          {/* Guided protocol questions + Notes */}
          <div className="flex flex-col flex-1 gap-4 min-w-0 min-h-0">
            <GuidedProtocolQuestions />
            <Notes />
          </div>
        </div>

        {/* Footer – shrink-0 so it stays visible */}
        <div className="shrink-0 bg-white border-t border-light-gray-20 pt-4 mt-4">
          <Group justify="flex-end" gap="md">
            <Button
              variant="filled"
              color="#D6D8DA"
              size="md"
              onClick={handleCancel}
              disabled={isSubmitting}
            >
              <Text size="sm" fw={500} c="grey.11">
                Cancel
              </Text>
            </Button>
            <Button
              variant="filled"
              color="#254769"
              size="md"
              onClick={handleMainSubmit}
              loading={isSubmitting}
              disabled={isSubmitting}
            >
              {section === 'caller-information' ? 'Continue' : 'Create Case'}
            </Button>
          </Group>
        </div>
      </div>
    </div>
  )
}

export default CaseCreationModal
